/**
 * File manipulation utilities
 */

import {
  IMAGE_FILE_EXTENSIONS,
  POWERPOINT_FILE_EXTENSIONS,
  SPREADSHEET_FILE_EXTENSIONS,
  TEXT_FILE_EXTENSIONS,
  WORD_FILE_EXTENSIONS,
} from '@/constants/supported-file-types';

/**
 * Removes timestamp prefix from correspondence filenames.
 * Files ingested via correspondence are stored with a timestamp prefix
 * to avoid collisions in blob storage.
 *
 * @param filename - Filename with format: YYYYMMDD_HHMMSS_MMMM_ORIGINALNAME.EXT
 * @returns Original filename without timestamp prefix, or unchanged if no prefix found
 * @example removeTimestampPrefix("20250804_163103_7263_document.pdf") // "document.pdf"
 * @example removeTimestampPrefix("plain-file.pdf") // "plain-file.pdf"
 */
export const removeTimestampPrefix = (filename: string): string => {
  return filename.replace(/^\d{8}_\d{6}_\d+_(.+)$/, '$1');
};

export const NO_EMAIL_SUBJECT = 'No Subject';

export const isEmailFileName = (name?: string | null): boolean => {
  const lower = name?.toLowerCase() ?? '';
  return lower.endsWith('.eml') || lower.endsWith('.msg');
};

export const emailDisplayName = (
  fileName?: string | null,
  subject?: string | null,
): string | null => {
  if (!isEmailFileName(fileName)) return null;
  const clean = subject?.trim().replace(/\s+/g, ' ');
  return clean || NO_EMAIL_SUBJECT;
};

/** User-facing document label. Never falls back to an email's stored blob name. */
export const documentDisplayName = (doc: {
  displayName?: string | null;
  fileName?: string | null;
}): string => (
  doc.displayName
  || emailDisplayName(doc.fileName)
  || doc.fileName
  || 'Unknown Document'
);

/**
 * The file's own name, as stored on its file reference. Emails keep their
 * file name here even though their display name is the subject line.
 */
export const documentFileName = (doc: {
  displayName?: string | null;
  fileName?: string | null;
}): string => (
  isEmailFileName(doc.fileName)
    ? doc.fileName ?? ''
    : doc.displayName || doc.fileName || ''
);

/**
 * Extracts the file extension from a filename, including the leading dot.
 *
 * @param name - Filename or path
 * @returns Extension with dot (e.g. ".pdf"), or empty string if none
 * @example getFileExtension("report.pdf") // ".pdf"
 * @example getFileExtension("archive.tar.gz") // ".gz"
 * @example getFileExtension("README") // ""
 */
export const getFileExtension = (name: string): string => {
  const lastDot = name.lastIndexOf('.');
  return lastDot > 0 ? name.substring(lastDot) : '';
};

export const isSpreadsheetFileName = (name: string | undefined | null): boolean => {
  const lowerName = name?.toLowerCase() ?? '';
  return SPREADSHEET_FILE_EXTENSIONS.some(extension => lowerName.endsWith(extension));
};

export const isWordFileName = (name: string | undefined | null): boolean => {
  const lowerName = name?.toLowerCase() ?? '';
  return WORD_FILE_EXTENSIONS.some(extension => lowerName.endsWith(extension));
};

export const isPowerPointFileName = (name: string | undefined | null): boolean => {
  const lowerName = name?.toLowerCase() ?? '';
  return POWERPOINT_FILE_EXTENSIONS.some(extension => lowerName.endsWith(extension));
};

export const isImageFileName = (name: string | undefined | null): boolean => {
  const lowerName = name?.toLowerCase() ?? '';
  return IMAGE_FILE_EXTENSIONS.some(extension => lowerName.endsWith(extension));
};

export const isTextFileName = (name: string | undefined | null): boolean => {
  const lowerName = name?.toLowerCase() ?? '';
  return TEXT_FILE_EXTENSIONS.some(extension => lowerName.endsWith(extension));
};

/**
 * Strips the timestamp prefix and file extension from a filename,
 * returning a clean display name.
 *
 * @param name - Raw filename (may include timestamp prefix and extension)
 * @returns Display-friendly name without prefix or extension
 * @example cleanFileName("20250804_163103_7263_Contract Draft.pdf") // "Contract Draft"
 * @example cleanFileName("photo.jpg") // "photo"
 */
export const cleanFileName = (name: string): string => {
  const stripped = removeTimestampPrefix(name);
  const lastDot = stripped.lastIndexOf('.');
  return lastDot > 0 ? stripped.substring(0, lastDot) : stripped;
};

/**
 * Generates a temporary client-side ID with a "temp-" prefix.
 * Used for optimistic UI elements that haven't been persisted yet.
 *
 * @returns A unique temporary ID, e.g. "temp-a1b2c3d4-..."
 */
export const tempId = (): string => `temp-${crypto.randomUUID()}`;
